import { useCallback, useMemo, useState } from 'react';
import {
  IndexedParticipation,
  SelectionData,
  SelectionPayload,
} from '@app/api';

interface Props {
  sessionId: string;
  totalCount: number;
  onFiltersChange?: () => void;
}

export const useParticipationTableSelection = ({
  sessionId,
  totalCount,
}: Props) => {
  const [selectionMode, setSelectionMode] = useState<'partial' | 'all'>(
    'partial',
  );
  const [selectedParticipations, setSelectedParticipations] = useState<
    IndexedParticipation[]
  >([]);
  const [deselectedIds, setDeselectedIds] = useState<string[]>([]);
  const [selectionData, setSelectionData] = useState<SelectionData | null>(
    null,
  );

  const selectedCount =
    selectionMode === 'all'
      ? totalCount - deselectedIds.length
      : selectedParticipations.length;

  const isAllSelected = useMemo(() => {
    return (
      (selectionMode === 'all' && deselectedIds.length === 0) ||
      selectedParticipations?.length + deselectedIds?.length === totalCount
    );
  }, [deselectedIds, selectedParticipations, selectionMode, totalCount]);

  const onDataChange = useCallback((selectionFilter: SelectionData) => {
    setSelectionData(selectionFilter);
  }, []);

  const deselectAll = () => {
    setSelectionMode('partial');
    setSelectedParticipations([]);
    setDeselectedIds([]);
  };

  const selectAll = useCallback(
    (participations: IndexedParticipation[]) => {
      if (selectionMode !== 'all') {
        setSelectionMode('all');
        setSelectedParticipations(participations);
        setDeselectedIds([]);
      } else {
        setSelectionMode('partial');
        setSelectedParticipations([]);
        setDeselectedIds([]);
      }
    },
    [selectionMode],
  );

  const select = useCallback(
    (participation: IndexedParticipation) => {
      const checked = !!selectedParticipations.find(
        (val) => val.id === participation.id,
      );

      if (selectionMode === 'partial') {
        if (checked) {
          const newValues = selectedParticipations.filter(
            (value) => value.id !== participation.id,
          );
          setSelectedParticipations(newValues);
        } else {
          setSelectedParticipations([...selectedParticipations, participation]);
        }
      } else if (selectionMode === 'all') {
        if (checked) {
          setDeselectedIds([...deselectedIds, participation.id]);
          setSelectedParticipations(
            selectedParticipations.filter((p) => p.id !== participation.id),
          );
        } else {
          setDeselectedIds(
            deselectedIds.filter((id) => id !== participation.id),
          );
          setSelectedParticipations([...selectedParticipations, participation]);
        }
      }
    },
    [deselectedIds, selectedParticipations, selectionMode],
  );

  const syncSelection = useCallback(
    (loadedParticipations: IndexedParticipation[]) => {
      if (selectionMode === 'all') {
        const newParticipations = loadedParticipations.filter(
          (p) =>
            !deselectedIds.includes(p.id) &&
            !selectedParticipations.some((sp) => sp.id === p.id),
        );

        if (newParticipations.length > 0) {
          setSelectedParticipations([
            ...selectedParticipations,
            ...newParticipations,
          ]);
        }
      }
    },
    [selectionMode, deselectedIds, selectedParticipations],
  );

  const canSendIds = useMemo(() => {
    if (selectionMode === 'all') {
      if (
        selectedParticipations?.length + deselectedIds?.length ===
        totalCount
      ) {
        return true;
      }
    } else {
      return true;
    }

    return false;
  }, [deselectedIds, selectedParticipations, selectionMode, totalCount]);

  return {
    selectionMode,
    selectionData,
    selectedParticipations,
    deselectedIds,
    selectedCount,
    isAllSelected,

    select,
    selectAll,
    deselectAll,
    syncSelection,
    onDataChange,

    getSelectionPayload: (): SelectionPayload => ({
      sessionId: sessionId,
      selectionMode: selectionMode,
      deselectedIds,
      selectionData: selectionData,
      selectedIds: canSendIds ? selectedParticipations.map((p) => p.id) : [],
      selectedParticipations: selectedParticipations,
    }),
  };
};
